import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useAuth } from '../contexts/AuthContext';

import { apiUrl } from '../utils/apiBase';
const API_BASE = apiUrl('/api/playlists');

export const usePlaylistManager = (targetUser = null) => {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [apiAvailable, setApiAvailable] = useState(true); // Traccia se le API sono disponibili
  const [availableUsers, setAvailableUsers] = useState([]);
  const authenticatedFetch = useAuthenticatedFetch();
  const { user: currentUser } = useAuth();

  // Verifica se le API sono disponibili
  const checkApiAvailability = useCallback(async () => {
    try {
      console.log('🔍 Verifica disponibilità API playlist...');
      const response = await authenticatedFetch(API_BASE, { 
        method: 'HEAD',
        signal: AbortSignal.timeout(5000) // Timeout di 5 secondi
      });
      console.log('✅ API playlist disponibili:', response.ok);
      setApiAvailable(response.ok);
      return response.ok;
    } catch (err) {
      console.warn('❌ API playlist non disponibili:', err.message);
      setApiAvailable(false);
      return false;
    }
  }, [authenticatedFetch]);

  // Carica lista utenti disponibili (solo per admin)
  const loadAvailableUsers = useCallback(async () => {
    if (currentUser?.role !== 'admin') return;
    
    try {
      const response = await authenticatedFetch(`${API_BASE}/users`);
      if (response.ok) {
        const users = await response.json();
        setAvailableUsers(users);
      }
    } catch (err) {
      console.warn('Errore nel caricare utenti:', err);
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Carica tutte le playlist (dell'utente corrente o target se admin)
  const loadPlaylists = useCallback(async (userToLoad = null) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Caricamento playlist...');
      
      // Costruisci URL con parametro utente se specificato e se admin
      let url = API_BASE;
      if (userToLoad && currentUser?.role === 'admin') {
        url += `?user=${encodeURIComponent(userToLoad)}`;
        console.log(`👑 Admin caricamento playlist per utente: ${userToLoad}`);
      }
      
      console.log('📡 Richiesta playlist URL:', url);
      const response = await authenticatedFetch(url);
      
      if (!response.ok) {
        // Se API non disponibili, imposta il flag
        if (response.status === 401) {
          throw new Error('Autenticazione fallita');
        } else if (response.status >= 500) {
          setApiAvailable(false);
          throw new Error('Server non disponibile');
        } else {
          throw new Error(`Errore ${response.status}: ${response.statusText}`);
        }
      }
      
      const data = await response.json();
      console.log('✅ Playlist caricate:', data.length);
      setPlaylists(data);
      setApiAvailable(true); // Se arriviamo qui, le API funzionano
      
    } catch (err) {
      console.error('❌ Errore nel caricamento playlist:', err);
      setError(err.message);
      
      // Se errore di autenticazione o timeout, marca API come non disponibili
      if (err.message.includes('Sessione scaduta') || err.message.includes('timeout') || err.message.includes('Server non disponibile')) {
        setApiAvailable(false);
      }
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Upload di una nuova playlist (sempre per l'utente corrente)
  const uploadPlaylist = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Inizio upload file:', file.name, 'Dimensione:', file.size);

      const formData = new FormData();
      formData.append('playlist', file);

      // Aggiungi timeout di 60 secondi per l'upload
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      try {
        const response = await authenticatedFetch(`${API_BASE}/upload`, {
          method: 'POST',
          body: formData,
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        console.log('Risposta upload ricevuta:', response.status);

        if (!response.ok) {
          let errorMessage = 'Errore nell\'upload';
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch (parseError) {
            // Se la risposta non è JSON, usa il testo della risposta o status
            try {
              const errorText = await response.text();
              errorMessage = errorText || `Errore HTTP ${response.status}`;
            } catch {
              errorMessage = `Errore HTTP ${response.status}`;
            }
          }
          throw new Error(errorMessage);
        }

        let result;
        try {
          result = await response.json();
        } catch (parseError) {
          throw new Error('Risposta del server non valida (non JSON)');
        }
        
        console.log('Upload completato con successo:', result);
        
        // Ricarica la lista dopo l'upload
        await loadPlaylists();
        
        return result.playlist;
        
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('Upload interrotto: timeout di 60 secondi superato');
        }
        throw fetchError;
      }
    } catch (err) {
      console.error('Errore nell\'upload:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadPlaylists, authenticatedFetch]);

  // Carica una playlist da URL
  const uploadPlaylistFromUrl = useCallback(async (url) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(`${API_BASE}/upload-url`, {
        method: 'POST',
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Errore nel caricamento da URL');
      }

      const result = await response.json();
      await loadPlaylists();
      return result.playlist;
    } catch (err) {
      console.error('Errore upload da URL:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, loadPlaylists]);

  // Aggiorna una playlist dal suo URL
  const refreshPlaylistFromUrl = useCallback(async (playlistId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(`${API_BASE}/${playlistId}/refresh`, {
        method: 'POST'
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Errore nell\'aggiornamento della playlist');
      }

      const result = await response.json();
      setPlaylists(prev => prev.map(p => (p.id === playlistId ? result.playlist : p)));
      return result.playlist;
    } catch (err) {
      console.error('Errore refresh playlist:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  // Toggle selezione playlist (supporta utente target per admin)
  const togglePlaylist = useCallback(async (playlistId, userToToggle = null) => {
    try {
      setError(null);

      // Costruisci URL con parametro utente se specificato e se admin
      let url = `${API_BASE}/${playlistId}/toggle`;
      if (userToToggle && currentUser?.role === 'admin') {
        url += `?user=${encodeURIComponent(userToToggle)}`;
      }

      const response = await authenticatedFetch(url, {
        method: 'PUT'
      });

      if (!response.ok) {
        throw new Error('Errore nel toggle della playlist');
      }

      const result = await response.json();
      
      // Aggiorna lo stato locale
      setPlaylists(prev => prev.map(p => 
        p.id === playlistId ? { ...p, selected: result.playlist.selected } : p
      ));

      return result.playlist;
    } catch (err) {
      console.error('Errore nel toggle playlist:', err);
      setError(err.message);
      throw err;
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Elimina playlist (supporta utente target per admin)
  const deletePlaylist = useCallback(async (playlistId, userToDelete = null) => {
    try {
      setError(null);

      // Costruisci URL con parametro utente se specificato e se admin
      let url = `${API_BASE}/${playlistId}`;
      if (userToDelete && currentUser?.role === 'admin') {
        url += `?user=${encodeURIComponent(userToDelete)}`;
      }

      const response = await authenticatedFetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Errore nell\'eliminazione della playlist');
      }

      // Rimuovi dalla lista locale
      setPlaylists(prev => prev.filter(p => p.id !== playlistId));
      
      return true;
    } catch (err) {
      console.error('Errore nell\'eliminazione playlist:', err);
      setError(err.message);
      throw err;
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Ottieni contenuto delle playlist selezionate (per l'utente corrente)
  const getSelectedPlaylistsContent = useCallback(async () => {
    try {
      setError(null);

      const response = await authenticatedFetch(`${API_BASE}/selected/content`);
      if (!response.ok) {
        throw new Error('Errore nel caricamento delle playlist selezionate');
      }

      const result = await response.json();
      return result;
    } catch (err) {
      console.error('Errore nel caricamento contenuto playlist:', err);
      setError(err.message);
      throw err;
    }
  }, [authenticatedFetch]);

  // Download playlist
  const downloadPlaylist = useCallback(async (playlistId, userToDownload = null) => {
    try {
      setError(null);

      // Costruisci URL con parametro utente se specificato e se admin
      let url = `${API_BASE}/${playlistId}/download`;
      if (userToDownload && currentUser?.role === 'admin') {
        url += `?user=${encodeURIComponent(userToDownload)}`;
      }

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error('Errore nel download della playlist');
      }

      // Ottieni il blob del file
      const blob = await response.blob();
      
      // Ottieni il nome del file dall'header Content-Disposition
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'playlist.m3u';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Crea un link temporaneo per il download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Pulisci
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return true;
    } catch (err) {
      console.error('Errore nel download playlist:', err);
      setError(err.message);
      throw err;
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Rinomina playlist (supporta utente target per admin)
  const renamePlaylist = useCallback(async (playlistId, newName, userToRename = null) => {
    try {
      setError(null);

      // Costruisci URL con parametro utente se specificato e se admin
      let url = `${API_BASE}/${playlistId}/rename`;
      if (userToRename && currentUser?.role === 'admin') {
        url += `?user=${encodeURIComponent(userToRename)}`;
      }

      const response = await authenticatedFetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ newName })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore nella rinomina della playlist');
      }

      const result = await response.json();
      
      // Aggiorna lo stato locale
      setPlaylists(prev => prev.map(p => 
        p.id === playlistId ? result.playlist : p
      ));

      return result.playlist;
    } catch (err) {
      console.error('Errore nella rinomina playlist:', err);
      setError(err.message);
      throw err;
    }
  }, [authenticatedFetch, currentUser?.role]);

  // Carica tutti i file (playlist + favorites) per l'interfaccia
  const loadAllFiles = useCallback(async (userToLoad = null) => {
    try {
      setLoading(true);
      setError(null);
      
      const targetUsername = userToLoad || currentUser?.username;
      
      // Carica le playlist normali
      await loadPlaylists(userToLoad);
      
      // Aggiungi il file favorites se esiste
      const allFiles = [...playlists];
      
      // Simula un file favorites (verrà mostrato nell'interfaccia se esiste)
      const favoritesFile = {
        id: 'favorites',
        originalName: `${targetUsername}_favorites.m3u`,
        fileName: `${targetUsername}.m3u`,
        filePath: `/uploads/favorites/${targetUsername}.m3u`,
        uploadDate: new Date().toISOString(),
        channelCount: 0, // Sarà calcolato se necessario
        selected: false,
        isFavorites: true,
        type: 'favorites'
      };
      
      return [...allFiles, favoritesFile];
      
    } catch (err) {
      console.error('Errore nel caricamento di tutti i file:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadPlaylists, playlists, currentUser?.username]);

  // Carica le playlist all'inizializzazione (solo una volta)
  useEffect(() => {
    const initLoad = async () => {
      console.log('🔄 Inizializzazione playlist manager per utente:', currentUser?.username);
      await loadPlaylists(targetUser);
      if (currentUser?.role === 'admin') {
        await loadAvailableUsers();
      }
    };
    if (currentUser && !loading) {
      initLoad();
    }
  }, [currentUser?.username, currentUser?.role, targetUser]); // Solo dipendenze primitive che non cambiano spesso

  // Getter per playlist selezionate
  const selectedPlaylists = playlists.filter(p => p.selected);
  const hasSelectedPlaylists = selectedPlaylists.length > 0;

  return {
    // Stato
    playlists,
    selectedPlaylists,
    hasSelectedPlaylists,
    availableUsers,
    loading,
    error,
    apiAvailable,

    // Azioni
    loadPlaylists,
    loadAvailableUsers,
    loadAllFiles,
    uploadPlaylist,
    uploadPlaylistFromUrl,
    togglePlaylist,
    deletePlaylist,
    refreshPlaylistFromUrl,
    getSelectedPlaylistsContent,
    checkApiAvailability,
    downloadPlaylist,
    renamePlaylist,

    // Utils
    clearError: () => setError(null)
  };
};
